/**
 * ItemListView -- Catalog of ecosystem items: n8n workflows, shared components, artifacts.
 * Uses NxListViewer (shared component v1.0) for rendering.
 * First artifact built following the "Crear Artefacto" template v1.0.
 *
 * PROJECT-ITEMS-HUB-001 / T4 (150 NXS):
 *   Tercera barra de filtro: PROYECTO. Cuando se selecciona uno, se llama el
 *   endpoint /api/v1/Projects/by-key/{key}/items, se construye un indice por
 *   item_ref+item_type, y la tabla muestra SOLO items vinculados al proyecto
 *   con columnas extra: Relacion (canon/uses/exposes/hosts), Proyecto (chip)
 *   y Donde vive (file_path/route/external_id de metadata).
 *
 *   Cuando filtro proyecto = 'all', la tabla mantiene su forma original
 *   (mismas columnas, todos los items de ITEM_CATALOG).
 *
 * SALV-T9 parte 2 (Ares #20511): la fuente es la BASE, no la lista fija. La vista pide GET /api/v1/Projects/items
 * (SALV-T8, nexo-api 374: todos los nodos, con su proyecto y la lectura con respaldo) y pinta cada nodo con su
 * proyecto, donde vive y el SELLO de quien lo verifico (el mismo de #/projects/:key), mas el filtro "Sin verificar".
 * El filtro de proyecto filtra en local (ya vienen todos). Si la API no contesta, queda el catalogo fijo de siempre y
 * se DICE por que (sin sesion, sin permiso, ambiente dev, o no hubo respuesta): nunca una lista corta muda.
 *
 * @module presentation/views/ItemListView
 */

import { ITEM_CATALOG, ITEM_TYPES, ITEM_STATUSES } from '../../domain/entities/Item.js';
import { TIPOS_NODO, itemsDesdeRespuesta, filtrarNodos } from '../../domain/nodosDelCatalogo.js';
import { cuandoLegible } from '../../domain/verificacionNodo.js';
import { renderNxList, bindNxListEvents, NX_LIST_VIEWER_VERSION } from '../shared/NxListViewer.js';
import { ProjectApiClient }      from '../../infrastructure/api/ProjectApiClient.js';
import { ProjectItemsApiClient } from '../../infrastructure/api/ProjectItemsApiClient.js';
import { AppConfig } from '../../config.js';

// Deuda 4: el escape local solo cambiaba "<" y este archivo lo usa dentro de atributos con comilla doble.
import { esc as escHtml } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

const RELATION_COLOR = {
  canon:   '#a371f7',
  hosts:   '#58a6ff',
  exposes: '#3fb950',
  uses:    '#8b949e',
};

let currentTypeFilter = 'all';
let currentCategoryFilter = 'all';
let currentProjectFilter = 'all';
let soloSinVerificar = false;

// SALV-T9: de donde salen las filas. modo 'api' = todos los nodos de la base; 'fijo' = ITEM_CATALOG (la API no contesto).
let _fuente = { modo: 'fijo', items: ITEM_CATALOG, aviso: '' };

// Cache T4: lista de proyectos + items por proyecto seleccionado.
let _projectsList = null;
// Map clave: itemRef|itemType (lowercase) -> { relation, metadata, projectKey, projectDisplay }
let _projectItemsIndex = null;
let _indexedProjectKey = null;

const tipoDe = (type) => ITEM_TYPES[type] || TIPOS_NODO[type] || { label: type, color: '#6e7681', icon: '' };

function typeBadge(type) {
  const t = tipoDe(type);
  return `<span class="item-type-badge" style="background:${t.color}22;color:${t.color}">${t.icon} ${t.label}</span>`;
}

function statusDot(status, estado) {
  const s = ITEM_STATUSES[status] || { label: status, color: '#6e7681' };
  const dice = estado && estado.toLowerCase() !== s.label ? estado : s.label;
  return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${s.color};margin-right:4px" title="${escHtml(dice)}"></span>${escHtml(dice)}`;
}

function versionBadge(v) {
  return v ? `<span class="version-badge">v${escHtml(String(v).replace(/^v/i, ''))}</span>` : '';
}

/** El sello de #/projects/:key, en una celda: quien y cuando, o "Sin verificar". */
function selloCelda(sello) {
  if (!sello) return '<span style="color:#484f58">—</span>';
  return sello.verificado
    ? `<span data-sello="verificado" style="font-size:0.875rem;color:#3fb950" title="${escHtml(sello.quien)}">✔ ${escHtml(sello.quien.split(/\s/)[0])}${sello.cuando ? ` · ${escHtml(cuandoLegible(sello.cuando))}` : ''}</span>`
    : '<span data-sello="sin-verificar" style="font-size:0.875rem;color:#d29922">⏳ Sin verificar</span>';
}

function relBadge(rel) {
  if (!rel) return '<span style="color:#484f58">—</span>';
  const c = RELATION_COLOR[rel] || '#6e7681';
  return `<span class="proj-badge" style="background:${c}22;color:${c};font-size:0.875rem">${escHtml(rel)}</span>`;
}

function projectChip(projectKey, projectDisplay) {
  if (!projectKey) return '<span style="color:#484f58">—</span>';
  return `<a href="#/projects/${encodeURIComponent(projectKey)}" class="task-tag" style="background:#58a6ff22;color:#58a6ff;text-decoration:none">${escHtml(projectDisplay || projectKey)}</a>`;
}

function whereLives(metadataStr, fallback) {
  if (!metadataStr) return fallback || '';
  let meta = metadataStr;
  if (typeof metadataStr === 'string') {
    try { meta = JSON.parse(metadataStr); } catch { return fallback || ''; }
  }
  return meta?.file_path || meta?.route || meta?.table || meta?.external_id || fallback || '';
}

function usedByLinks(usedBy) {
  if (!usedBy || usedBy.length === 0) return '<span style="color:#484f58">--</span>';
  return usedBy.map(u => `<a href="${u}" class="item-used-link">${escHtml(u)}</a>`).join(' ');
}

function getCategories(items) {
  const cats = new Set();
  items.forEach(i => {
    const cat = i.metadata?.category;
    if (cat) cats.add(cat);
  });
  return [...cats].sort();
}

/**
 * Por que se ve el catalogo fijo y no la base (F3 del molde: un mensaje por causa y por ambiente). La sesion vencida
 * ya la maneja ApiBase (401 -> salir); aqui quedan los demas.
 */
export function avisoDeFuente(r, env = AppConfig.env, apiBase = AppConfig.apiBase) {
  if (r.ok) return '';
  const fijo = `Se muestra el catalogo fijo (${ITEM_CATALOG.length} items), no los nodos de la base.`;
  if (r.code === 403 || r.code === 'FORBIDDEN') return `${fijo} Tu usuario no tiene permiso para leer los nodos.`;
  if (env === 'dev') return `${fijo} Estas en ambiente dev: el panel le pregunta a tu API local (${apiBase}) y no obtuvo la lista. En produccion esta consulta SI existe.`;
  if (r.code === 404) return `${fijo} La API de este ambiente todavia no tiene la consulta de todos los nodos; en cuanto la publique, esto se enciende solo.`;
  if (r.code === 'FORMA') return `${fijo} La API contesto, pero sin la lista de nodos.`;
  return `${fijo} No pudimos hablar con la API en este momento.`;
}

async function cargarFuente() {
  let r;
  try { r = itemsDesdeRespuesta(await ProjectItemsApiClient.getAll()); } catch (e) { r = { ok: false, code: 'NETWORK', error: e?.message }; }
  _fuente = r.ok
    ? { modo: 'api', items: r.items, aviso: '' }
    : { modo: 'fijo', items: ITEM_CATALOG, aviso: avisoDeFuente(r) };
  return _fuente;
}

export async function renderItemList(container) {
  currentTypeFilter = 'all';
  currentCategoryFilter = 'all';
  currentProjectFilter = 'all';
  soloSinVerificar = false;
  _projectItemsIndex = null;
  _indexedProjectKey = null;

  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Items / Objetos</h1>
          <p style="font-size:0.875rem;color:#8b949e;margin-top:2px" id="nx-item-fuente">Leyendo los nodos de la base&hellip; &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}</p>
        </div>
      </div>
      <div id="nx-item-aviso" hidden style="margin-top:6px;font-size:0.875rem;color:#d29922"></div>
      <div id="nx-item-filtros"></div>
      <div id="nx-item-list" style="margin-top:12px"></div>

      <div id="nx-item-detail" hidden style="margin-top:12px;padding:12px;border:1px solid #21262d;border-radius:8px;background:#161b22"></div>
    </section>`;

  const fuente = await cargarFuente();
  if (!document.getElementById('nx-item-filtros')) return; // se navego a otra vista mientras cargaba
  const items = fuente.items;
  const categories = getCategories(items);
  const tipos = [...new Set(items.map(i => i.type))].sort();
  const sinSello = fuente.modo === 'api' ? items.filter(i => i.status !== 'inactive' && !i._sello?.verificado).length : 0;

  document.getElementById('nx-item-fuente').innerHTML = fuente.modo === 'api'
    ? `${items.length} nodos de la base (nexo_catalogs.project_items) &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}`
    : `Catalogo del ecosistema &middot; NxListViewer v${NX_LIST_VIEWER_VERSION}`;
  const $aviso = document.getElementById('nx-item-aviso');
  $aviso.hidden = !fuente.aviso;
  $aviso.textContent = fuente.aviso;

  document.getElementById('nx-item-filtros').innerHTML = `
      <div class="task-filter-bar" id="nx-item-type-filter">
        <button class="btn-filter active" data-itype="all">Todos (${items.length})</button>
        ${tipos.map((key) => {
          const t = tipoDe(key);
          const count = items.filter(i => i.type === key).length;
          return `<button class="btn-filter" data-itype="${escHtml(key)}">${t.icon} ${t.label} (${count})</button>`;
        }).join('')}
        ${fuente.modo === 'api' ? `<button class="btn-filter" data-sinverificar style="margin-left:8px">⏳ Sin verificar (${sinSello})</button>` : ''}
      </div>

      ${categories.length ? `<div class="task-tag-filter-bar" id="nx-item-cat-filter" style="margin-top:4px">
        <span class="filter-label">Categoria:</span>
        <button class="btn-tag-filter active" data-icat="all">Todas</button>
        ${categories.map(c => `<button class="btn-tag-filter" data-icat="${escHtml(c)}">${escHtml(c)}</button>`).join('')}
      </div>` : ''}

      <div class="task-tag-filter-bar" id="nx-item-proj-filter" style="margin-top:4px">
        <span class="filter-label">Proyecto:</span>
        <button class="btn-tag-filter active" data-iproj="all">Todos</button>
        <span id="nx-item-proj-buttons" style="display:contents;color:#8b949e;font-size:0.875rem">cargando...</span>
        <span id="nx-item-proj-status" style="margin-left:8px;font-size:0.875rem;color:#6e7681"></span>
      </div>`;

  setupFilters();
  renderList();

  if (fuente.modo === 'api') {
    // Ya vienen todos con su proyecto: los botones salen de los mismos nodos, sin otra consulta.
    const claves = [...new Set(items.map(i => i._projectKey).filter(Boolean))].sort();
    document.getElementById('nx-item-proj-buttons').innerHTML = claves.map(k => {
      const n = items.filter(i => i._projectKey === k).length;
      return `<button class="btn-tag-filter" data-iproj="${escHtml(k)}">${escHtml(k)} (${n})</button>`;
    }).join('');
    bindProjectFilterButtons();
  } else {
    // Carga lazy de proyectos para poblar la 3a barra de filtro (no bloquea el primer render)
    loadProjectsAsync();
  }
}

async function loadProjectsAsync() {
  if (_projectsList) {
    const $c = document.getElementById('nx-item-proj-buttons');
    if ($c) $c.innerHTML = _projectsList.map(p => `<button class="btn-tag-filter" data-iproj="${escHtml(p.projectKey)}" title="${escHtml(p.displayName || '')}">${escHtml(p.projectKey)}</button>`).join('');
    bindProjectFilterButtons();
    return _projectsList;
  }

  const $btnContainer = document.getElementById('nx-item-proj-buttons');
  if (!$btnContainer) return null;

  try {
    const res = await ProjectApiClient.list(1, 50);
    if (!res.ok) {
      $btnContainer.innerHTML = `<span style="color:#f85149">error</span>`;
      return null;
    }
    const payload = res.data?.data ?? res.data;
    _projectsList = payload?.items || [];

    // Render botones de proyectos
    $btnContainer.innerHTML = _projectsList.map(p => `
      <button class="btn-tag-filter" data-iproj="${escHtml(p.projectKey)}" title="${escHtml(p.displayName || '')}">${escHtml(p.projectKey)}</button>
    `).join('');

    // Re-bindear eventos para los nuevos botones
    bindProjectFilterButtons();
    return _projectsList;
  } catch (err) {
    $btnContainer.innerHTML = `<span style="color:#f85149">error</span>`;
    return null;
  }
}

function setupFilters() {
  const $typeBar = document.getElementById('nx-item-type-filter');
  $typeBar.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-filter');
    if (!btn) return;
    if (btn.hasAttribute('data-sinverificar')) {
      // "Sin verificar" se suma al filtro de tipo (no lo reemplaza): es lo que Josse tiene que mirar.
      soloSinVerificar = !soloSinVerificar;
      btn.classList.toggle('active', soloSinVerificar);
      renderList();
      return;
    }
    $typeBar.querySelectorAll('.btn-filter:not([data-sinverificar])').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentTypeFilter = btn.dataset.itype;
    renderList();
  });

  const $catBar = document.getElementById('nx-item-cat-filter');
  if ($catBar) $catBar.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-tag-filter');
    if (!btn) return;
    $catBar.querySelectorAll('.btn-tag-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    currentCategoryFilter = btn.dataset.icat;
    renderList();
  });

  bindProjectFilterButtons();
}

function bindProjectFilterButtons() {
  const $projBar = document.getElementById('nx-item-proj-filter');
  if (!$projBar) return;
  // Re-bind: clonamos el listener via new handler (no acumula porque este listener se setea una vez en setupFilters
  // pero los botones nuevos los agregamos despues; usamos delegation para no necesitar re-bind).
  if ($projBar._delegationBound) return;
  $projBar._delegationBound = true;
  $projBar.addEventListener('click', async (e) => {
    const btn = e.target.closest('.btn-tag-filter');
    if (!btn) return;
    $projBar.querySelectorAll('.btn-tag-filter').forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    const newKey = btn.dataset.iproj;
    if (newKey === currentProjectFilter) { renderList(); return; }
    currentProjectFilter = newKey;
    if (_fuente.modo !== 'api') await refreshProjectIndex();
    renderList();
  });
}

async function refreshProjectIndex() {
  const $status = document.getElementById('nx-item-proj-status');
  if (currentProjectFilter === 'all') {
    _projectItemsIndex = null;
    _indexedProjectKey = null;
    if ($status) $status.textContent = '';
    return;
  }
  if (_indexedProjectKey === currentProjectFilter && _projectItemsIndex) {
    return; // ya indexado
  }
  if ($status) $status.textContent = `cargando items de ${currentProjectFilter}...`;
  try {
    const res = await ProjectItemsApiClient.getByKey(currentProjectFilter);
    if (!res.ok) {
      _projectItemsIndex = new Map();
      _indexedProjectKey = currentProjectFilter;
      if ($status) $status.textContent = `error: ${res.error || 'no disponible'}`;
      return;
    }
    const data = res.data?.data || res.data || {};
    const all = Array.isArray(data.all) ? data.all : [];
    const projectKey = data.projectKey || currentProjectFilter;
    const projectDisplay = data.displayName || projectKey;
    const idx = new Map();
    for (const r of all) {
      const k = `${String(r.itemRef).toLowerCase()}|${String(r.itemType).toLowerCase()}`;
      // Si el mismo (ref,type) aparece con varias relations, prevalece canon > exposes > hosts > uses
      const prev = idx.get(k);
      const rank = (rel) => ({ canon:0, exposes:1, hosts:2, uses:3 })[rel] ?? 9;
      if (!prev || rank(r.relation) < rank(prev.relation)) {
        idx.set(k, { relation: r.relation, metadata: r.metadata, projectKey, projectDisplay });
      }
    }
    _projectItemsIndex = idx;
    _indexedProjectKey = currentProjectFilter;
    if ($status) $status.textContent = `${idx.size} items vinculados`;
  } catch (err) {
    _projectItemsIndex = new Map();
    _indexedProjectKey = currentProjectFilter;
    if ($status) $status.textContent = 'error de red';
  }
}

function renderList() {
  if (_fuente.modo === 'api') { renderListaDeNodos(); return; }
  let items = [...ITEM_CATALOG];

  if (currentTypeFilter !== 'all') {
    items = items.filter(i => i.type === currentTypeFilter);
  }
  if (currentCategoryFilter !== 'all') {
    items = items.filter(i => i.metadata?.category === currentCategoryFilter);
  }

  // Filtro proyecto: si hay un proyecto seleccionado, solo mostrar items vinculados.
  // Y enriquecer cada item con relation + projectKey + whereLives desde el indice.
  const projectFilterActive = currentProjectFilter !== 'all' && _projectItemsIndex;
  if (projectFilterActive) {
    items = items
      .map(i => {
        const k = `${String(i.id).toLowerCase()}|${itemTypeToProjectItemType(i.type)}`;
        const hit = _projectItemsIndex.get(k);
        if (!hit) return null;
        return Object.assign({}, i, {
          _relation: hit.relation,
          _projectKey: hit.projectKey,
          _projectDisplay: hit.projectDisplay,
          _whereLives: whereLives(hit.metadata, i.metadata?.file || i.metadata?.route),
        });
      })
      .filter(Boolean);
  }

  // Construccion de columnas: si hay proyecto, agrega 3 columnas extra; si no, layout original.
  const baseColumns = [
    { key: 'name',        label: 'Nombre',     width: '26%', render: (v) => `<strong>${escHtml(v)}</strong>` },
    { key: 'type',        label: 'Tipo',       width: '11%', render: (v) => typeBadge(v) },
    { key: 'version',     label: 'Version',    width: '7%',  render: (v) => versionBadge(v) },
    { key: 'status',      label: 'Estado',     width: '9%',  render: (v) => statusDot(v) },
    { key: 'description', label: 'Descripcion',              render: (v) => `<span style="font-size:0.875rem;color:#8b949e">${escHtml(v || '').substring(0, 80)}${(v || '').length > 80 ? '…' : ''}</span>` },
    { key: 'usedBy',      label: 'Usado por',  width: '14%', render: (v) => usedByLinks(v) }
  ];

  const projectColumns = projectFilterActive ? [
    { key: '_relation',       label: 'Relacion',   width: '9%',  render: (v) => relBadge(v) },
    { key: '_projectKey',     label: 'Proyecto',   width: '11%', render: (_v, row) => projectChip(row._projectKey, row._projectDisplay) },
    { key: '_whereLives',     label: 'Donde vive', width: '15%', render: (v) => v ? `<code style="font-size:0.875rem;color:#6e7681">${escHtml(v)}</code>` : '<span style="color:#484f58">—</span>' },
  ] : [];

  const columns = projectFilterActive
    ? [
        baseColumns[0],
        baseColumns[1],
        baseColumns[2],
        baseColumns[3],
        ...projectColumns,
        // En modo proyecto sustituimos description+usedBy por algo mas compacto: solo description corta
        Object.assign({}, baseColumns[4], { width: '20%' }),
      ]
    : baseColumns;

  const listConfig = {
    id: 'nx-item-table',
    columns,
    data: items,
    sortBy: projectFilterActive ? '_relation' : 'type',
    sortDir: 'asc',
    emptyMessage: projectFilterActive
      ? `Ningun item del catalogo coincide con los items vinculados al proyecto ${escHtml(currentProjectFilter)}. Verifica que esten registrados en project_items.`
      : 'No hay items con ese filtro'
  };

  const $list = document.getElementById('nx-item-list');
  $list.innerHTML = renderNxList(listConfig);

  bindNxListEvents($list, 'nx-item-table', listConfig, (row) => {
    showDetail(row);
  });
}

/** SALV-T9: la tabla con los nodos de la base. Proyecto, donde vive y el sello van siempre (ya vienen en cada nodo). */
function renderListaDeNodos() {
  const items = filtrarNodos(_fuente.items, { tipo: currentTypeFilter, categoria: currentCategoryFilter, proyecto: currentProjectFilter, sinVerificar: soloSinVerificar });
  const columns = [
    { key: 'name',        label: 'Nodo',        width: '22%', render: (v) => `<strong>${escHtml(v)}</strong>` },
    { key: 'type',        label: 'Tipo',        width: '11%', render: (v) => typeBadge(v) },
    { key: '_projectKey', label: 'Proyecto',    width: '11%', render: (_v, row) => projectChip(row._projectKey) },
    { key: 'status',      label: 'Estado',      width: '9%',  render: (v, row) => statusDot(v, row.estado) },
    { key: '_whereLives', label: 'Donde vive',  width: '17%', render: (v) => v ? `<code style="font-size:0.875rem;color:#6e7681">${escHtml(String(v).substring(0, 60))}</code>` : '<span style="color:#484f58">—</span>' },
    { key: '_verificado', label: 'Verificado',  width: '14%', render: (_v, row) => selloCelda(row._sello) },
    { key: 'description', label: 'Descripcion',               render: (v) => `<span style="font-size:0.875rem;color:#8b949e">${escHtml(v || '').substring(0, 70)}${(v || '').length > 70 ? '…' : ''}</span>` }
  ];
  const listConfig = {
    id: 'nx-item-table',
    columns,
    data: items.map(i => Object.assign({}, i, { _verificado: i._sello?.verificado ? 1 : 0 })),
    sortBy: '_projectKey',
    sortDir: 'asc',
    emptyMessage: soloSinVerificar ? 'Todos los nodos con este filtro ya tienen quien los verifico.' : 'No hay nodos con ese filtro'
  };
  const $list = document.getElementById('nx-item-list');
  $list.innerHTML = renderNxList(listConfig);
  bindNxListEvents($list, 'nx-item-table', listConfig, (row) => showDetail(row));
}

/**
 * Mapea ITEM_CATALOG.type (workflow|component|artifact|infrastructure) a project_items.item_type
 * (instruction|form|endpoint|view|capability|workflow|component|infrastructure|table).
 *
 * En el seed actual:
 *   - 'artifact' (panel-v2 hosts) se sembraron como item_type='view'.
 *   - 'workflow' -> 'workflow'.
 *   - 'component' -> 'component'.
 *   - 'infrastructure' -> 'infrastructure'.
 */
function itemTypeToProjectItemType(catalogType) {
  switch (catalogType) {
    case 'artifact':       return 'view';
    case 'workflow':       return 'workflow';
    case 'component':      return 'component';
    case 'infrastructure': return 'infrastructure';
    default:               return catalogType;
  }
}

function showDetail(item) {
  const $detail = document.getElementById('nx-item-detail');
  $detail.hidden = false;

  const metaEntries = Object.entries(item.metadata || {})
    .map(([k, v]) => `<span style="font-size:0.875rem"><strong>${escHtml(k)}:</strong> ${escHtml(typeof v === 'object' ? JSON.stringify(v) : String(v))}</span>`)
    .join(' &middot; ');

  const projectInfo = item._projectKey
    ? `<span><strong>Proyecto:</strong> ${projectChip(item._projectKey, item._projectDisplay)}</span>
       <span><strong>Relacion:</strong> ${relBadge(item._relation)}</span>
       ${item._whereLives ? `<span><strong>Donde vive:</strong> <code>${escHtml(item._whereLives)}</code></span>` : ''}`
    : '';
  // SALV-T9: el sello completo (quien, cuando, que falta) y el dueno, como en #/projects/:key.
  const s = item._sello;
  const selloInfo = s
    ? `<div data-sello-detalle style="margin-top:6px;font-size:0.875rem;color:${s.verificado ? '#3fb950' : '#d29922'}">${s.verificado
        ? `✔ Verificado${s.cuando ? ` el ${escHtml(cuandoLegible(s.cuando))}` : ''} por ${escHtml(s.quien)}`
        : '⏳ Sin verificar'}${s.falta ? `<br><span style="color:#d29922">Falta comprobar: ${escHtml(s.falta)}</span>` : ''}${item._dueno ? `<br><span style="color:#8b949e">Dueno: ${escHtml(item._dueno)}</span>` : ''}</div>`
    : '';

  $detail.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:start">
      <div>
        <h3 style="margin:0;font-size:1rem">${escHtml(item.name)} ${typeBadge(item.type)} ${versionBadge(item.version)}</h3>
        <p style="font-size:0.875rem;color:#c9d1d9;margin-top:6px">${escHtml(item.description)}</p>
      </div>
      <button class="btn-ghost btn-sm" id="nx-item-close-detail" style="flex-shrink:0">&times; Cerrar</button>
    </div>
    <div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:8px;font-size:0.875rem;color:#8b949e">
      <span><strong>ID:</strong> ${escHtml(item.id)}</span>
      ${item.externalId ? `<span><strong>External:</strong> ${escHtml(item.externalId)}</span>` : ''}
      <span><strong>Estado:</strong> ${statusDot(item.status, item.estado)}</span>
      <span><strong>Usado por:</strong> ${usedByLinks(item.usedBy)}</span>
      ${projectInfo}
    </div>
    ${selloInfo}
    ${metaEntries ? `<div style="margin-top:8px;color:#6e7681">${metaEntries}</div>` : ''}`;

  document.getElementById('nx-item-close-detail').onclick = () => { $detail.hidden = true; };
  $detail.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}
